import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { contactService } from "@/services/contactService";
import { useGlobalSearchParams } from "expo-router";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import useNavigationGuard from "@/hooks/useNavigationGuard";

interface ContactRef {
  id: string;
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  email_address?: string;
  phone_number?: string;
}

interface ContactsInputProps {
  /** Array of contact objects */
  value: ContactRef[];
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (contacts: ContactRef[]) => void;
}

/** Helper to get display name: preferred_name OR first_name + last_name */
function getDisplayName(contact: ContactRef): string {
  if (contact.preferred_name) return contact.preferred_name;
  return [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unknown";
}

export function ContactsInput({ value, state = "editing", label = "Contacts", error, onChange }: ContactsInputProps) {
  const currentParams = useGlobalSearchParams<{ panel?: string; id?: string }>();
  const { pushNavigation } = useNavigationHistory();
  const currentEntity = useCurrentEntity();
  const { navigateToEntity } = useNavigationGuard();

  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  const handleChipPress = useCallback(
    (contactId: string) => {
      const currentPanel = Array.isArray(currentParams.panel) ? currentParams.panel[0] : currentParams.panel;
      const currentId = Array.isArray(currentParams.id) ? currentParams.id[0] : currentParams.id;
      if (currentPanel && currentId) {
        pushNavigation({ panel: currentPanel, id: currentId, title: currentEntity?.currentTitle || "" });
      }
      navigateToEntity("contact", contactId);
    },
    [currentParams, pushNavigation, currentEntity, navigateToEntity],
  );

  // Fetch all contact options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const contacts = await contactService.getContacts();
        if (cancelled) return;
        setOptions(
          contacts.map((c) => ({
            id: c.id!,
            name: getDisplayName({
              id: c.id!,
              first_name: c.first_name ?? undefined,
              last_name: c.last_name ?? undefined,
              preferred_name: (c as any).preferred_name ?? undefined,
              email_address: c.email_address ?? undefined,
              phone_number: c.phone_number ?? undefined,
            }),
            description: c.email_address ?? c.phone_number ?? undefined,
            avatar: {
              firstName: c.first_name ?? undefined,
              lastName: c.last_name ?? undefined,
              email: c.email_address ?? undefined,
            },
          })),
        );
      } catch (err) {
        console.error("Failed to load contacts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items from value
  const chipItems = useMemo<ChipItem[]>(() => {
    return value.map((contact) => ({
      id: contact.id,
      name: getDisplayName(contact),
      readonly: false,
      avatar: {
        firstName: contact.first_name,
        lastName: contact.last_name,
        email: contact.email_address,
      },
    }));
  }, [value]);

  const handleAdd = useCallback(
    (contactId: string) => {
      if (value.some((c) => c.id === contactId)) return;
      const opt = options.find((o) => o.id === contactId);
      if (!opt || !opt.avatar) return;
      onChange?.([
        ...value,
        {
          id: opt.id,
          first_name: opt.avatar.firstName,
          last_name: opt.avatar.lastName,
          preferred_name: undefined,
          email_address: opt.avatar.email,
        },
      ]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (contactId: string) => {
      onChange?.(value.filter((c) => c.id !== contactId));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="account-box"
      accentTone="sky"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search contacts..."
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onChipPress={handleChipPress}
    />
  );
}
